import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Warning, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { riskApplicationService } from '../../../services/riskApplicationService';
import { unitService } from '../../../services/unitService';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import type { RiskApplication } from '../../../types/riskApplication';
import {
  ExposureLevel,
  ExposureLevelLabels,
  ExposureLevelColors,
  ApplicationLevel,
  ApplicationLevelLabels,
} from '../../../types/riskApplication';

interface RiskWithApplication {
  id: number;
  riskId: number;
  riskName: string;
  riskFactorName: string;
  exposureLevel: ExposureLevel;
  applicationLevel: ApplicationLevel;
}

function toRow(app: RiskApplication): RiskWithApplication {
  return {
    id: app.id,
    riskId: app.riskId,
    riskName: app.risk?.riskFactorName ?? app.risk?.risk ?? `Risco ${app.riskId}`,
    riskFactorName: app.risk?.riskFactorName ?? '',
    exposureLevel: app.exposureLevel,
    applicationLevel: app.scope,
  };
}

function sortByExposureLevel(risks: RiskWithApplication[]): RiskWithApplication[] {
  const order: Record<ExposureLevel, number> = {
    [ExposureLevel.VERY_HIGH]: 0,
    [ExposureLevel.HIGH]: 1,
    [ExposureLevel.MEDIUM]: 2,
    [ExposureLevel.LOW]: 3,
  };
  return [...risks].sort((a, b) => order[a.exposureLevel] - order[b.exposureLevel]);
}

interface EmployeeRisksCardProps {
  employeeId: number;
  companyId: number;
  departmentId: number;
  occupationId: number;
  /** A unidade do setor do funcionário: é por ela que o aplicado à unidade chega a ele (#541). */
  unidadeDoSetorId?: number | string | null;
  /** A unidade do cadastro do funcionário, que só serve ao aviso quando difere da do setor. */
  unidadeDoFuncionario?: { id: number | string; name: string } | null;
}

/**
 * A unidade do funcionário não alcança os riscos dele quando o setor é de outra unidade, ou de
 * nenhuma. Os ids chegam como texto do backend: compara-se convertendo.
 */
function unidadeForaDoSetor(
  unidadeDoFuncionario: EmployeeRisksCardProps['unidadeDoFuncionario'],
  unidadeDoSetorId: EmployeeRisksCardProps['unidadeDoSetorId']
): boolean {
  if (!unidadeDoFuncionario) return false;
  return unidadeDoSetorId == null || String(unidadeDoFuncionario.id) !== String(unidadeDoSetorId);
}

const ACCENT = '#D32F2F';
const PAGE_SIZE = 5;

export const EmployeeRisksCard: React.FC<EmployeeRisksCardProps> = ({
  employeeId,
  companyId,
  departmentId,
  occupationId,
  unidadeDoSetorId = null,
  unidadeDoFuncionario = null,
}) => {
  const [risks, setRisks] = useState<RiskWithApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const avisarUnidade = unidadeForaDoSetor(unidadeDoFuncionario, unidadeDoSetorId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      riskApplicationService.list('company', companyId).catch(() => []),
      riskApplicationService.list('department', departmentId).catch(() => []),
      riskApplicationService.list('occupation', occupationId).catch(() => []),
      riskApplicationService.list('employee', employeeId).catch(() => []),
      unidadeDoSetorId == null
        ? Promise.resolve([])
        : riskApplicationService.list('unit', Number(unidadeDoSetorId)).catch(() => []),
    ])
      .then(([comp, dept, occ, emp, unid]) => {
        if (cancelled) return;
        const all = [
          ...comp.map(toRow),
          ...unid.map(toRow),
          ...dept.map(toRow),
          ...occ.map(toRow),
          ...emp.map(toRow),
        ];
        setRisks(sortByExposureLevel(all));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Erro ao carregar riscos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [employeeId, companyId, departmentId, occupationId, unidadeDoSetorId]);

  // O nome da unidade do setor não vem no cadastro do funcionário: só se busca quando há aviso, e
  // só a quem lê unidade — a ficha não exige `unit.read`, e sem ele o aviso sai sem o nome.
  const carregarUnidadeDoSetor = useCallback(async () => {
    if (unidadeDoSetorId == null) return null;
    const unidade = await unitService.getUnitById(Number(unidadeDoSetorId));
    return { id: String(unidadeDoSetorId), name: unidade.name };
  }, [unidadeDoSetorId]);
  const unidadeDoSetor = useDadoPermitido('unit.read', 'Unidade do setor', carregarUnidadeDoSetor, {
    habilitado: avisarUnidade && unidadeDoSetorId != null,
  }).dado;

  const nomeDaUnidadeDoSetor =
    unidadeDoSetor && unidadeDoSetor.id === String(unidadeDoSetorId) ? unidadeDoSetor.name : null;
  const unidadeDoSetorNaFrase = (): string => {
    if (unidadeDoSetorId == null) return 'não tem unidade';
    return nomeDaUnidadeDoSetor ? `é da unidade ${nomeDaUnidadeDoSetor}` : 'é de outra unidade';
  };

  const criticalCount = risks.filter(
    (r) => r.exposureLevel === ExposureLevel.HIGH || r.exposureLevel === ExposureLevel.VERY_HIGH
  ).length;

  const totalPages = Math.ceil(risks.length / PAGE_SIZE);
  const paged = risks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><Warning fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Riscos Ocupacionais
          </Typography>
          {!loading && !error && (
            <Chip label={risks.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {avisarUnidade && unidadeDoFuncionario && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0.5, mb: 1.5 }}>
            {`Este funcionário está na unidade ${unidadeDoFuncionario.name}, mas o setor dele ` +
              `${unidadeDoSetorNaFrase()}. Os riscos da unidade ${unidadeDoFuncionario.name} não chegam a ele.`}
          </Alert>
        )}

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {error && <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{error}</Alert>}

        {!loading && !error && risks.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nenhum registro encontrado</Typography>
        )}

        {!loading && !error && risks.length > 0 && (
          <>
            {/* Summary */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              <Chip label={`${risks.length} aplicados`} size="small" variant="outlined" />
              {criticalCount > 0 && (
                <Chip label={`${criticalCount} críticos`} size="small" color="error" variant="outlined" />
              )}
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((risk) => (
                <Box
                  key={`${risk.applicationLevel}-${risk.id}`}
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Chip
                    label={ApplicationLevelLabels[risk.applicationLevel]}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.6rem', minWidth: 56 }}
                  />
                  <Typography variant="caption" sx={{ flex: 1 }} noWrap title={risk.riskName}>
                    {risk.riskName}
                  </Typography>
                  <Chip
                    label={ExposureLevelLabels[risk.exposureLevel]}
                    size="small"
                    color={ExposureLevelColors[risk.exposureLevel]}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, risks.length)} de {risks.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
